import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { useState, memo } from 'react'
import {
  Briefcase,
  Award,
  CheckCircle,
  Search,
  PenTool,
  Rocket,
  Building2,
  BarChart3,
  Megaphone,
  Users,
  ArrowRight,
} from 'lucide-react'
import { ContactCTA } from '@/components/sections'
import consulenzaHeroImg from '@/assets/images/sections/ConsulenzaTeaserHome.jpg'
import undergroundSpaImg from '@/assets/images/sections/UndergroundSpa.jpg'

// Service data with workflow steps
const services = [
  {
    id: 'new_opening',
    icon: Building2,
    color: 'from-blue-400 to-indigo-500',
    bgColor: 'bg-blue-50',
    accentColor: 'text-blue-600',
  },
  {
    id: 'business',
    icon: BarChart3,
    color: 'from-blue-500 to-indigo-600',
    bgColor: 'bg-indigo-50',
    accentColor: 'text-indigo-600',
  },
  {
    id: 'management',
    icon: Megaphone,
    color: 'from-indigo-500 to-blue-600',
    bgColor: 'bg-blue-50',
    accentColor: 'text-blue-600',
  },
  {
    id: 'recruiting',
    icon: Users,
    color: 'from-blue-600 to-indigo-700',
    bgColor: 'bg-indigo-50',
    accentColor: 'text-indigo-600',
  },
]

// BF Consulting Badge
const ConsultingBadge = memo(function ConsultingBadge() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-4 py-2 rounded-full shadow-lg"
    >
      <Briefcase className="w-5 h-5" />
      <span className="font-semibold tracking-wide">BF CONSULTING</span>
    </motion.div>
  )
})

// Method pillars visualization
const MethodPillars = memo(function MethodPillars({ t }: { t: (key: string) => string }) {
  const pillars = [
    { key: 'analyze', icon: Search, color: 'from-blue-400 to-indigo-500' },
    { key: 'design', icon: PenTool, color: 'from-blue-500 to-indigo-600' },
    { key: 'implement', icon: Rocket, color: 'from-blue-600 to-indigo-700' },
  ]

  return (
    <div className="grid md:grid-cols-3 gap-6">
      {pillars.map((pillar, index) => {
        const Icon = pillar.icon
        return (
          <motion.div
            key={pillar.key}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.15 }}
            className="relative group"
          >
            {/* Glassmorphism card */}
            <div className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-white/80 backdrop-blur-sm border border-white/50 p-4 sm:p-6 shadow-xl hover:shadow-2xl transition-all duration-300 group-hover:-translate-y-1">
              {/* Background gradient orb */}
              <div
                className={`absolute -top-10 -right-10 w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gradient-to-br ${pillar.color} opacity-20 blur-2xl group-hover:opacity-30 transition-opacity`}
              />

              {/* Icon */}
              <div
                className={`relative w-11 h-11 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl bg-gradient-to-br ${pillar.color} flex items-center justify-center mb-3 sm:mb-4 shadow-lg`}
              >
                <Icon className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
              </div>

              {/* Content */}
              <h3 className="text-base sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2">
                {t(`method.pillars.${pillar.key}.title`)}
              </h3>
              <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                {t(`method.pillars.${pillar.key}.description`)}
              </p>
            </div>
          </motion.div>
        )
      })}
    </div>
  )
})

export function Consulting() {
  const { t, i18n } = useTranslation('consulting')
  const [activeService, setActiveService] = useState(0)

  // Get workflow steps for active service
  const workflowSteps = t(`services.${services[activeService].id}.workflow`, {
    returnObjects: true,
  }) as { title: string; description: string }[]

  return (
    <>
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('meta.title')}</title>
        <meta name="description" content={t('meta.description')} />
      </Helmet>

      {/* Hero Section with Parallax Effect */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src={consulenzaHeroImg}
            alt="BF Wellness Consulting"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/80 to-gray-900/60" />
        </div>

        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-64 h-64 rounded-full bg-white/5"
              initial={{ opacity: 0 }}
              animate={{
                opacity: [0.1, 0.3, 0.1],
                scale: [1, 1.2, 1],
                x: [0, 30, 0],
                y: [0, -20, 0],
              }}
              transition={{
                duration: 8 + i * 2,
                repeat: Infinity,
                delay: i * 1.5,
              }}
              style={{
                left: `${20 + i * 15}%`,
                top: `${10 + i * 20}%`,
              }}
            />
          ))}
        </div>

        {/* Content */}
        <div className="relative container-custom py-32">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <ConsultingBadge />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl md:text-6xl font-bold text-white mt-6 mb-6"
            >
              {t('hero.title')}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl md:text-2xl text-white/90 leading-relaxed mb-8"
            >
              {t('hero.subtitle')}
            </motion.p>

            {/* Method Keywords */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-wrap gap-3"
            >
              {['ANALIZZARE', 'PROGETTARE', 'REALIZZARE'].map((word) => (
                <span
                  key={word}
                  className="px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold text-sm"
                >
                  {word}
                </span>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2"
          >
            <div className="w-1 h-2 rounded-full bg-white/60" />
          </motion.div>
        </motion.div>
      </section>

      {/* Method Section - ANALIZZARE, PROGETTARE, REALIZZARE */}
      <section className="section-padding bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 opacity-50 blur-3xl -translate-y-1/2 translate-x-1/2" />

        <div className="container-custom relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('method.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t('method.subtitle')}</p>
          </motion.div>

          <MethodPillars t={t as (key: string) => string} />
        </div>
      </section>

      {/* Services Section with Horizontal Tabs */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('services.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t('services.subtitle')}</p>
          </motion.div>

          {/* Horizontal Tab Navigation */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-8 sm:mb-10">
            {services.map((service, index) => {
              const Icon = service.icon
              return (
                <motion.button
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  onClick={() => setActiveService(index)}
                  className={`relative flex items-center gap-2 sm:gap-3 px-3 py-2 sm:px-5 sm:py-3 rounded-full transition-all duration-300 ${
                    activeService === index
                      ? `bg-gradient-to-r ${service.color} text-white shadow-lg scale-105`
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="font-semibold text-xs sm:text-sm md:text-base whitespace-nowrap">
                    {t(`services.${service.id}.title`)}
                  </span>
                </motion.button>
              )
            })}
          </div>

          {/* Service Content */}
          <motion.div
            key={activeService}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            {/* Service Header */}
            <div className={`${services[activeService].bgColor} rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 mb-6 sm:mb-8`}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mb-2">
                <div
                  className={`w-11 h-11 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl bg-gradient-to-br ${services[activeService].color} flex items-center justify-center shadow-lg`}
                >
                  {(() => {
                    const Icon = services[activeService].icon
                    return <Icon className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                  })()}
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                    {t(`services.${services[activeService].id}.workflow_title`)}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600">
                    {t(`services.${services[activeService].id}.subtitle`)}
                  </p>
                </div>
              </div>
            </div>

            {/* Workflow Steps Grid */}
            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
              {Array.isArray(workflowSteps) &&
                workflowSteps.map((step, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="relative bg-white rounded-lg sm:rounded-xl p-4 sm:p-6 shadow-md border border-gray-100 hover:shadow-lg transition-shadow group"
                  >
                    {/* Step Number */}
                    <div
                      className={`absolute -top-2 -left-2 sm:-top-3 sm:-left-3 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br ${services[activeService].color} flex items-center justify-center shadow-md`}
                    >
                      <span className="text-white font-bold text-sm sm:text-base">{index + 1}</span>
                    </div>

                    {/* Content */}
                    <div className="pt-1 sm:pt-2">
                      <h4
                        className={`font-bold text-base sm:text-lg text-gray-900 mb-1 sm:mb-2 ${services[activeService].accentColor}`}
                      >
                        {step.title}
                      </h4>
                      <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">{step.description}</p>
                    </div>

                    {/* Decorative corner */}
                    <div
                      className={`absolute bottom-0 right-0 w-12 h-12 sm:w-16 sm:h-16 rounded-tl-full bg-gradient-to-br ${services[activeService].color} opacity-5 group-hover:opacity-10 transition-opacity`}
                    />
                  </motion.div>
                ))}
            </div>

            {/* CTA */}
            <div className="text-center mt-8 sm:mt-10">
              <motion.a
                href="#contact"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className={`inline-flex items-center gap-2 px-6 py-3 sm:px-8 sm:py-4 rounded-lg sm:rounded-xl bg-gradient-to-r ${services[activeService].color} text-white text-sm sm:text-base font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-105`}
              >
                {t('services.cta')}
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {t('why.title')}
              </h2>
              <p className="text-lg text-gray-600 mb-8">{t('why.description')}</p>

              <div className="space-y-4">
                {(t('why.points', { returnObjects: true }) as string[]).map((point, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center mt-0.5">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">{point}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={undergroundSpaImg}
                  alt="BF Wellness Consulting"
                  className="w-full h-auto"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 to-transparent" />
              </div>

              {/* Floating card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="absolute -bottom-4 -left-2 sm:-bottom-6 sm:-left-6 bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-xl border border-gray-100"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">{t('why.badge.title')}</div>
                    <div className="text-sm text-gray-600">{t('why.badge.subtitle')}</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <ContactCTA />
    </>
  )
}
